import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { formatDate, formatTime, relativeDue } from '../lib/format'
import { supabase } from '../lib/supabase'
import type { ChecklistItem, KioskIdentity, KioskStaff, KioskTask, StartedKioskTask } from '../types'
import { BrandMark } from './BrandMark'

const DEVICE_TOKEN_KEY = 'ventuhi_compliance_device_token'

type AnswerValue = boolean | string | number | null

function beep() {
  const AudioContextConstructor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!AudioContextConstructor) return
  const context = new AudioContextConstructor()
  const oscillator = context.createOscillator()
  const gain = context.createGain()
  oscillator.type = 'sine'
  oscillator.frequency.setValueAtTime(740, context.currentTime)
  gain.gain.setValueAtTime(0.0001, context.currentTime)
  gain.gain.exponentialRampToValueAtTime(0.22, context.currentTime + 0.02)
  gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.65)
  oscillator.connect(gain)
  gain.connect(context.destination)
  oscillator.start()
  oscillator.stop(context.currentTime + 0.7)
  oscillator.onended = () => void context.close()
}

function PairingScreen({ onActivated }: { onActivated: (token: string) => void }) {
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function activate(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    const { data, error: rpcError } = await supabase.rpc('activate_compliance_kiosk', { p_pairing_code: code })
    setBusy(false)
    if (rpcError) return setError(rpcError.message)
    const result = Array.isArray(data) ? data[0] : data
    localStorage.setItem(DEVICE_TOKEN_KEY, result.device_token)
    onActivated(result.device_token)
  }

  return (
    <main className="kiosk-pairing">
      <section className="kiosk-pairing__card">
        <BrandMark />
        <span className="eyebrow">Device pairing</span>
        <h1>Set up Compliance kiosk</h1>
        <p>Enter the six-digit code generated from the manager workspace.</p>
        <form className="form-stack" onSubmit={activate}>
          <input
            className="pairing-input"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            aria-label="Six-digit pairing code"
            autoFocus
            required
          />
          {error && <div className="message message--error">{error}</div>}
          <button className="button button--primary button--large" disabled={busy || code.length !== 6}>{busy ? 'Pairing…' : 'Pair this device'}</button>
        </form>
        <a className="text-link" href="/">Return to manager sign-in</a>
      </section>
    </main>
  )
}

function TaskButton({ task, onSelect }: { task: KioskTask; onSelect: (task: KioskTask) => void }) {
  return (
    <button className={`kiosk-task kiosk-task--${task.alert_state}`} onClick={() => onSelect(task)}>
      <span className="kiosk-task__stripe" aria-hidden="true" />
      <span className="kiosk-task__body">
        <span className="kiosk-task__topline">
          <strong>{task.checklist_name}</strong>
          <span>{task.area_name ?? 'Venue-wide'}</span>
        </span>
        <span className="kiosk-task__meta">
          <span>{task.progress_label ?? relativeDue(task.due_at)}</span>
          <span>Due {formatTime(task.due_at)}</span>
        </span>
      </span>
      <span className="kiosk-task__action">Start</span>
    </button>
  )
}

function AnswerControl({
  item,
  value,
  onChange,
}: {
  item: ChecklistItem
  value: AnswerValue
  onChange: (value: AnswerValue) => void
}) {
  if (item.item_type === 'instruction') {
    return <div className="instruction-item">{item.help_text ?? item.label}</div>
  }

  if (item.item_type === 'confirmation') {
    return (
      <label className={`check-item ${value === true ? 'check-item--checked' : ''}`}>
        <input type="checkbox" checked={value === true} onChange={(event) => onChange(event.target.checked)} />
        <span className="check-item__box">✓</span>
        <span><strong>{item.label}</strong>{item.help_text && <small>{item.help_text}</small>}</span>
      </label>
    )
  }

  if (item.item_type === 'pass_fail') {
    return (
      <fieldset className="answer-card">
        <legend>{item.label}</legend>
        {item.help_text && <p>{item.help_text}</p>}
        <div className="segmented-control">
          <button type="button" className={value === 'pass' ? 'active' : ''} onClick={() => onChange('pass')}>Pass</button>
          <button type="button" className={value === 'fail' ? 'active fail' : ''} onClick={() => onChange('fail')}>Fail</button>
        </div>
      </fieldset>
    )
  }

  if (item.item_type === 'yes_no') {
    return (
      <fieldset className="answer-card">
        <legend>{item.label}</legend>
        <div className="segmented-control">
          <button type="button" className={value === true ? 'active' : ''} onClick={() => onChange(true)}>Yes</button>
          <button type="button" className={value === false ? 'active fail' : ''} onClick={() => onChange(false)}>No</button>
        </div>
      </fieldset>
    )
  }

  if (['number', 'temperature', 'measurement'].includes(item.item_type)) {
    return (
      <label className="answer-card">
        <strong>{item.label}</strong>
        {item.help_text && <small>{item.help_text}</small>}
        <input type="number" step="any" value={typeof value === 'number' || typeof value === 'string' ? value : ''} onChange={(event) => onChange(event.target.value)} />
      </label>
    )
  }

  return (
    <label className="answer-card">
      <strong>{item.label}</strong>
      {item.help_text && <small>{item.help_text}</small>}
      <textarea value={typeof value === 'string' ? value : ''} onChange={(event) => onChange(event.target.value)} rows={item.item_type === 'long_text' ? 4 : 2} />
    </label>
  )
}

export function KioskPortal() {
  const [deviceToken, setDeviceToken] = useState(() => localStorage.getItem(DEVICE_TOKEN_KEY))
  const [identity, setIdentity] = useState<KioskIdentity | null>(null)
  const [staff, setStaff] = useState<KioskStaff[]>([])
  const [tasks, setTasks] = useState<KioskTask[]>([])
  const [selectedTask, setSelectedTask] = useState<KioskTask | null>(null)
  const [selectedStaff, setSelectedStaff] = useState<KioskStaff | null>(null)
  const [pin, setPin] = useState('')
  const [startedTask, setStartedTask] = useState<StartedKioskTask | null>(null)
  const [staffSessionToken, setStaffSessionToken] = useState<string | null>(null)
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({})
  const [notes, setNotes] = useState('')
  const [alertsEnabled, setAlertsEnabled] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const lastAlertRef = useRef(0)

  const loadKiosk = useCallback(async () => {
    if (!deviceToken) return
    const [identityResult, staffResult, taskResult] = await Promise.all([
      supabase.rpc('get_compliance_kiosk_identity', { p_device_token: deviceToken }),
      supabase.rpc('get_compliance_kiosk_staff', { p_device_token: deviceToken }),
      supabase.rpc('get_compliance_kiosk_tasks', { p_device_token: deviceToken }),
    ])

    const rpcError = identityResult.error ?? staffResult.error ?? taskResult.error
    if (rpcError) {
      setError(rpcError.message)
      if (rpcError.message.toLowerCase().includes('not found') || rpcError.message.toLowerCase().includes('revoked')) {
        localStorage.removeItem(DEVICE_TOKEN_KEY)
        setDeviceToken(null)
      }
      return
    }

    const identityRow = Array.isArray(identityResult.data) ? identityResult.data[0] : identityResult.data
    setIdentity(identityRow as KioskIdentity)
    setStaff((staffResult.data ?? []) as KioskStaff[])
    setTasks((taskResult.data ?? []) as KioskTask[])
    setError(null)
  }, [deviceToken])

  useEffect(() => {
    void loadKiosk()
    const interval = window.setInterval(() => void loadKiosk(), 30_000)
    return () => window.clearInterval(interval)
  }, [loadKiosk])

  useEffect(() => {
    if (!alertsEnabled || !tasks.some((task) => task.sound_required)) return
    const now = Date.now()
    if (now - lastAlertRef.current > 60_000) {
      beep()
      lastAlertRef.current = now
    }
  }, [alertsEnabled, tasks])

  useEffect(() => {
    if (identity?.device_mode !== 'full_device') return
    const wakeLockNavigator = navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> } }
    let wakeLock: { release(): Promise<void> } | null = null
    void wakeLockNavigator.wakeLock?.request('screen').then((lock) => { wakeLock = lock }).catch(() => undefined)
    return () => { if (wakeLock) void wakeLock.release() }
  }, [identity?.device_mode])

  const buckets = useMemo(() => ({
    overdue: tasks.filter((task) => ['overdue', 'missed'].includes(task.alert_state)),
    due: tasks.filter((task) => ['due', 'due_soon'].includes(task.alert_state)),
    upcoming: tasks.filter((task) => task.alert_state === 'upcoming'),
  }), [tasks])

  function resetFlow() {
    setSelectedTask(null)
    setSelectedStaff(null)
    setPin('')
    setStartedTask(null)
    setStaffSessionToken(null)
    setAnswers({})
    setNotes('')
    setError(null)
  }

  async function authenticateAndStart(event: FormEvent) {
    event.preventDefault()
    if (!deviceToken || !selectedTask || !selectedStaff) return
    setBusy(true)
    setError(null)
    const { data: authData, error: authError } = await supabase.rpc('authenticate_compliance_kiosk_staff', {
      p_device_token: deviceToken,
      p_profile_id: selectedStaff.profile_id,
      p_pin: pin,
    })
    if (authError) {
      setBusy(false)
      setPin('')
      setError(authError.message)
      await loadKiosk()
      return
    }

    const authRow = Array.isArray(authData) ? authData[0] : authData
    if (authRow.profile_locked || !authRow.staff_session_token) {
      setBusy(false)
      setPin('')
      setError('This profile is temporarily locked. Ask a supervisor to unlock or reset the PIN.')
      await loadKiosk()
      return
    }

    const { data: taskData, error: taskError } = await supabase.rpc('start_compliance_task_kiosk', {
      p_device_token: deviceToken,
      p_staff_session_token: authRow.staff_session_token,
      p_task_instance_id: selectedTask.task_instance_id,
    })
    setBusy(false)
    if (taskError) return setError(taskError.message)
    const taskRow = Array.isArray(taskData) ? taskData[0] : taskData
    setStaffSessionToken(authRow.staff_session_token)
    setStartedTask(taskRow as StartedKioskTask)
    setAnswers({})
  }

  async function submitTask(event: FormEvent) {
    event.preventDefault()
    if (!deviceToken || !staffSessionToken || !startedTask) return
    const availableItems = startedTask.checklist_items.filter((item) => !item.already_completed && item.item_type !== 'instruction')
    const payload = availableItems
      .filter((item) => answers[item.id] !== undefined && answers[item.id] !== null && answers[item.id] !== '')
      .map((item) => {
        const value = answers[item.id]
        const passed = item.item_type === 'pass_fail' ? value === 'pass' : item.item_type === 'confirmation' ? value === true : undefined
        return {
          checklist_item_id: item.id,
          answer: { value },
          ...(passed === undefined ? {} : { passed }),
          ...(passed === false ? { failure_notes: notes || 'Failed during kiosk check.' } : {}),
        }
      })

    const requiredRemaining = availableItems.filter((item) => item.is_required)
    if (!startedTask.period_task && requiredRemaining.some((item) => !payload.some((answer) => answer.checklist_item_id === item.id))) {
      setError('Complete every required item before submitting.')
      return
    }
    if (payload.length === 0) {
      setError('Select at least one outstanding item to complete.')
      return
    }

    setBusy(true)
    setError(null)
    const { data: result, error: rpcError } = await supabase.rpc('submit_compliance_task_kiosk', {
      p_device_token: deviceToken,
      p_staff_session_token: staffSessionToken,
      p_submission_id: startedTask.submission_id,
      p_answers: payload,
      p_notes: notes || null,
    })
    setBusy(false)
    if (rpcError) return setError(rpcError.message)
    const row = Array.isArray(result) ? result[0] : result
    resetFlow()
    await loadKiosk()
    window.alert(row.outstanding_required_items > 0 ? `${row.completed_required_items} of ${row.total_required_items} jobs complete. The checklist remains open.` : 'Checklist completed. Thank you.')
  }

  async function enableAlerts() {
    setAlertsEnabled(true)
    beep()
    if (identity?.device_mode === 'full_device') {
      await document.documentElement.requestFullscreen?.().catch(() => undefined)
    }
  }

  function unpair() {
    if (!window.confirm('Remove this kiosk pairing from this browser? The manager can pair it again later.')) return
    localStorage.removeItem(DEVICE_TOKEN_KEY)
    setDeviceToken(null)
    setIdentity(null)
    resetFlow()
  }

  if (!deviceToken) return <PairingScreen onActivated={setDeviceToken} />

  if (!identity) {
    return <main className="loading-screen"><BrandMark /><div className="spinner" /><p>Opening paired kiosk…</p>{error && <div className="message message--error">{error}</div>}</main>
  }

  if (startedTask && selectedStaff) {
    return (
      <main className="kiosk-form-page">
        <header className="kiosk-form-header">
          <button className="button button--ghost" onClick={resetFlow}>Cancel</button>
          <div><span>{startedTask.area_name ?? 'Venue-wide'}</span><strong>{startedTask.task_title}</strong></div>
          <span>Completing as {selectedStaff.full_name}</span>
        </header>
        <form className="kiosk-checklist" onSubmit={submitTask}>
          <div className="kiosk-checklist__intro">
            <span className="eyebrow">{startedTask.period_task ? 'Complete one or more outstanding jobs' : 'Complete every required item'}</span>
            <h1>{startedTask.task_title}</h1>
            {startedTask.period_task && <p>Different staff can complete the remaining work later. The list stays open until every required job is finished.</p>}
          </div>
          <div className="kiosk-checklist__items">
            {startedTask.checklist_items.map((item) => item.already_completed ? (
              <div className="check-item check-item--completed" key={item.id}><span className="check-item__box">✓</span><span><strong>{item.label}</strong><small>Already completed</small></span></div>
            ) : (
              <AnswerControl key={item.id} item={item} value={answers[item.id] ?? null} onChange={(value) => setAnswers((current) => ({ ...current, [item.id]: value }))} />
            ))}
          </div>
          <label className="answer-card">
            <strong>Notes</strong>
            <small>Add context for a failed item or anything management should know.</small>
            <textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} />
          </label>
          {error && <div className="message message--error">{error}</div>}
          <button className="button button--primary button--jumbo" disabled={busy}>{busy ? 'Submitting…' : startedTask.period_task ? 'Save completed jobs' : 'Submit checklist'}</button>
        </form>
      </main>
    )
  }

  if (selectedTask) {
    return (
      <main className="kiosk-auth-page">
        <button className="button button--ghost kiosk-auth-page__back" onClick={resetFlow}>Back to tasks</button>
        <section className="kiosk-auth-card">
          <span className="eyebrow">{selectedTask.area_name ?? 'Venue-wide'}</span>
          <h1>{selectedTask.checklist_name}</h1>
          {!selectedStaff ? (
            <>
              <p>Select your name to start this check.</p>
              <div className="staff-picker">
                {staff.map((person) => {
                  const locked = Boolean(person.locked_until && new Date(person.locked_until) > new Date())
                  return (
                    <button key={person.profile_id} disabled={locked} onClick={() => setSelectedStaff(person)}>
                      <span className="avatar">{person.full_name.split(' ').map((part) => part[0]).slice(0, 2).join('')}</span>
                      <strong>{person.full_name}</strong>
                      {locked && <small>Profile locked</small>}
                    </button>
                  )
                })}
              </div>
            </>
          ) : (
            <form className="pin-form" onSubmit={authenticateAndStart}>
              <div className="avatar avatar--large">{selectedStaff.full_name.split(' ').map((part) => part[0]).slice(0, 2).join('')}</div>
              <strong>{selectedStaff.full_name}</strong>
              <p>Enter your four-digit PIN.</p>
              <input
                className="pin-entry"
                inputMode="numeric"
                pattern="[0-9]{4}"
                maxLength={4}
                value={pin}
                onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 4))}
                autoFocus
                aria-label="Four-digit staff PIN"
              />
              {error && <div className="message message--error">{error}</div>}
              <button className="button button--primary button--large" disabled={busy || pin.length !== 4}>{busy ? 'Checking…' : 'Start checklist'}</button>
              <button type="button" className="text-link button-link" onClick={() => { setSelectedStaff(null); setPin(''); setError(null) }}>Choose another name</button>
            </form>
          )}
        </section>
      </main>
    )
  }

  return (
    <main className={`kiosk-dashboard ${buckets.overdue.length ? 'kiosk-dashboard--alert' : ''}`}>
      <header className="kiosk-header">
        <BrandMark compact />
        <div className="kiosk-header__venue">
          <strong>{identity.venue_name}</strong>
          <span>{identity.device_name} · {identity.operational_areas.map((area) => area.name).join(', ')}</span>
        </div>
        <div className="kiosk-header__actions">
          {!alertsEnabled && <button className="button button--primary" onClick={() => void enableAlerts()}>Enable sound</button>}
          <button className="button button--ghost button--small" onClick={() => void loadKiosk()}>Refresh</button>
          <button className="button button--ghost button--small" onClick={unpair}>Manager setup</button>
        </div>
      </header>

      <section className="kiosk-hero">
        <div>
          <span className="eyebrow">{formatDate(new Date().toISOString())}</span>
          <h1>{buckets.overdue.length ? `${buckets.overdue.length} check${buckets.overdue.length === 1 ? '' : 's'} need attention` : 'Compliance tasks'}</h1>
          <p>{buckets.overdue.length ? 'The grace period has passed. Complete these checks now.' : 'Upcoming, due and longer-period work for this device.'}</p>
        </div>
        <div className="kiosk-clock" aria-label="Current time">
          {new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
        </div>
      </section>

      <div className="kiosk-columns">
        <section>
          <div className="kiosk-section-heading"><h2>Overdue</h2><span>{buckets.overdue.length}</span></div>
          <div className="kiosk-task-list">
            {buckets.overdue.map((task) => <TaskButton key={task.task_instance_id} task={task} onSelect={setSelectedTask} />)}
            {buckets.overdue.length === 0 && <div className="kiosk-empty kiosk-empty--good">Nothing overdue. Tiny victory parade.</div>}
          </div>
        </section>
        <section>
          <div className="kiosk-section-heading"><h2>Due now</h2><span>{buckets.due.length}</span></div>
          <div className="kiosk-task-list">
            {buckets.due.map((task) => <TaskButton key={task.task_instance_id} task={task} onSelect={setSelectedTask} />)}
            {buckets.due.length === 0 && <div className="kiosk-empty">No checks due right now.</div>}
          </div>
        </section>
        <section>
          <div className="kiosk-section-heading"><h2>Upcoming and period work</h2><span>{buckets.upcoming.length}</span></div>
          <div className="kiosk-task-list">
            {buckets.upcoming.map((task) => <TaskButton key={task.task_instance_id} task={task} onSelect={setSelectedTask} />)}
            {buckets.upcoming.length === 0 && <div className="kiosk-empty">No upcoming tasks for this device.</div>}
          </div>
        </section>
      </div>
    </main>
  )
}
